import React, { FC, useCallback, useEffect } from "react";
import CustomsDeclarationV2 from "components/CustomDeclaration/CustomsDeclarationV2";
import ParcelIcon from "components/Icon/icons/parcel.svg";
import {
  BackButton,
  Content,
  ContentWrapper,
  Heading,
  LeftArrowIcon,
} from "containers/RightPanel/RightPanel.styles";
import { format } from "date-fns";
import { ButtonColor } from "enums/Button";
import { IconType } from "enums/Icon";
import { List, Map } from "immutable";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { createStructuredSelector } from "reselect";
import { CustomInformationDto } from "types/api/cart";
import { OpenRightPanel } from "types/common/rightPanel";
import ImageViewer2 from "../../../../components/ImageViewer2/ImageViewer2";
import { openRightPanel } from "../../../../reduxFolder/reducers/rightPanel";
import { getRequestTypes } from "../../../../reduxFolder/reducers/serviceRequests";
import {
  getAddonsMethods,
  getConsolidationAddonsMethods,
} from "../../../../reduxFolder/reducers/services";
import { createCustomsDeclaration } from "../../../../reduxFolder/reducers/storage";
import { authSelectUnits } from "../../../../reduxFolder/selectors/globalSelectors";
import {
  historySelectDetailedShipment,
  historySelectDetailedShipmentLoading,
} from "../../../../reduxFolder/selectors/historySelector";
import {
  outgoingSelectDetailedShipment,
  outgoingSelectDetailedShipmentLoading,
} from "../../../../reduxFolder/selectors/outgoingSelectors";
import { requestsSelectServiceTypes } from "../../../../reduxFolder/selectors/serviceRequestsSelectors";
import { servicesSelectAddons } from "../../../../reduxFolder/selectors/servicesSelectors";
import {
  storageSelectDetailedLoading,
  storageSelectDetailedShipment,
  storageSelectParams,
} from "../../../../reduxFolder/selectors/storageSelectors";
import { getFnsLocale } from "../../../../translations/date-fns-locale";
import { toastResponseError } from "../../../../utils/responseMessageHelper";
import Tab from "../../../RightPanel/components/Tab";
import TabsView from "../../../RightPanel/components/TabsView";
import { useDemoHint } from "../DemoShipments/useDemoHint";
import InventoryItemComment from "../InventoryItemComment/InventoryItemComment";
import { InventoryItemCommentSkeleton } from "../InventoryItemComment/InventoryItemCommentSkeleton";
import ServicesButtons from "../ServiceButtons/ServicesButtons";
import ShipmentComment from "../ShipmentComment/ShipmentComment";
import {
  Container,
  ContentContainer,
  ImageSection,
  ParcelInfoContainer,
  ParcelInfoKey,
  ParcelInfoValue,
  ShipmentLogo,
  Spinner,
  SpinnerSection,
  Title,
} from "./StorageItemPanel.styles";

interface StorageItemPanelProps {
  isOpen: boolean;
  isLoading: boolean;
  isLoadingHistoryShipment: boolean;
  isLoadingOutgoingShipment: boolean;
  createCustomsDeclaration: (data: CustomInformationDto) => Promise<void>;
  panelData: any;
  historyDetailedShipment: any;
  outgoingDetailedShipment: any;
  detailedShipment: any;
  units: string;
  openRightPanel: OpenRightPanel;
  getRequestTypes: () => Promise<void>;
  serviceRequestsTypes: any;
  storageParams: any;
}

const StorageItemPanel: FC<StorageItemPanelProps> = React.memo(
  ({
    isOpen,
    isLoading: isLoadingProp,
    createCustomsDeclaration,
    getRequestTypes,
    serviceRequestsTypes,
    panelData,
    openRightPanel,
    detailedShipment,
    storageParams,
    outgoingDetailedShipment,
    isLoadingOutgoingShipment,
    historyDetailedShipment,
    isLoadingHistoryShipment,
    units,
  }) => {
    const { i18n, t } = useTranslation("common");
    const locale = getFnsLocale(i18n.language);
    const navigate = useNavigate();
    const { branchId: routeBranchId } = useParams();
    const id = panelData.get("id");
    const shipment =
      detailedShipment.get(id) ||
      outgoingDetailedShipment.get(id) ||
      historyDetailedShipment.get(id, Map());

    const readOnly = !!panelData.get("readOnly");
    const overrideAndEnablePackOptions = panelData.get(
      "overrideAndEnablePackOptions",
    );
    const isCartVariant = panelData.get("isCartVariant");
    const isDemo = !!panelData.get("isDemo");
    // Demo shipments are browse-only: the panel neutralizes its own fetches
    // and comment mutations, so demo callers only set the isDemo flag.
    const showDemoHint = useDemoHint();
    const demoStub = useCallback(() => {
      showDemoHint();
      return Promise.resolve();
    }, [showDemoHint]);
    const getShipment = panelData.get("getShipment");
    const updateComment = isDemo ? demoStub : panelData.get("updateComment");
    const updateItemComment = isDemo
      ? demoStub
      : panelData.get("updateItemComment");
    const showShipment = !!shipment.size;
    const isLoading =
      isLoadingProp || isLoadingOutgoingShipment || isLoadingHistoryShipment;
    const receivedAt = shipment.get("received_at") || new Date();
    const systemReceivedAt = Date.parse(receivedAt);
    const received = showShipment
      ? format(new Date(systemReceivedAt), "dd MMMM, y", {
          locale,
        })
      : "";
    const hasInventoryItems = panelData.get("hasInventoryItems");
    const preventImageZoom = panelData.get("preventImageZoom");

    // originalItem can either be parcel if coming from StorageList and in that case it doesn't have SKU prop loaded
    // if it has SKU loaded then it is inventory item if SKU starts with letter I
    const originalItem = panelData.get("originalItem", Map());
    const originalItemId = originalItem.get("id");
    const originalItemLatest =
      shipment &&
      shipment
        .get("items", List())
        .find((x: any) => x.get("id") === originalItemId);

    const originalItemSku = originalItem.get("sku");
    const isInventoryItem =
      typeof originalItemSku === "string" && originalItemSku.startsWith("I");
    let itemDetails = isInventoryItem
      ? shipment
          .get("items", List())
          .find((x: any) => x.get("id") === originalItem.get("id"))
      : shipment;
    if (!itemDetails) itemDetails = Map();

    // A shipment without items has no declarations to show — CustomsDeclarationV2
    // reads .toJS off this, so it must never be undefined.
    const declarations = isInventoryItem
      ? itemDetails.get("customs_declarations", List())
      : shipment.getIn(["items", 0, "customs_declarations"], List());
    const customsItemId = isInventoryItem ? originalItem.get("id") : id;

    useEffect(() => {
      if (!shipment.size && id && !isDemo)
        getShipment(id).catch(toastResponseError);
    }, [isOpen, getShipment, id, shipment.size, isDemo]);

    useEffect(() => {
      if (shipment.size && !panelData.get("preventReLoading") && !isDemo)
        getShipment(id).catch(toastResponseError);

      if (!serviceRequestsTypes) {
        getRequestTypes().catch(toastResponseError);
      }
    }, [
      id,
      getRequestTypes,
      serviceRequestsTypes,
      getShipment,
      shipment.size,
      panelData,
      isDemo,
    ]);

    const wrappedCreateCustomsDeclaration = useCallback(
      (params: any) =>
        createCustomsDeclaration({
          ...params,
          shipmentId: id,
          isInventoryItem,
        }),
      [createCustomsDeclaration, id, isInventoryItem],
    );

    const openServicePanel = useCallback(
      (requestType: string, isInventoryItemVariant?: boolean) => {
        if (isDemo) {
          showDemoHint();
          return;
        }

        // Special handling for Shopify export
        if (requestType === "exportToShopify") {
          openRightPanel(
            "SHOPIFY_EXPORT",
            panelData
              .set("exportItemId", originalItem.get("id"))
              .set("shipment", shipment)
              .set("preventReLoading", true)
              .set("returnScreen", "STORAGE_ITEM")
          );
          return;
        }

        // Existing service request handling
        openRightPanel(
          "SERVICE_REQUEST",
          panelData
            .set("shipment", shipment)
            .set("requests", shipment.get("service_requests"))
            .set("requestType", requestType)
            .set("preventReLoading", true)
            .set("requestInventoryShipmentData", shipment)
            .set("readOnly", readOnly)
            .set("isInventoryItemVariant", isInventoryItemVariant)
            .set("transferInventoryItemId", originalItem.get("id")),
        );
      },
      [
        openRightPanel,
        panelData,
        shipment,
        readOnly,
        originalItem,
        isDemo,
        showDemoHint,
      ],
    );

    const openPackOptions = useCallback(() => {
      if (isDemo) {
        showDemoHint();
        return;
      }

      openRightPanel(
        "PACK_OPTIONS",
        panelData
          .set("preventReLoading", true)
          .set("returnScreen", "STORAGE_ITEM")
          .set("readOnly", readOnly)
          .set("overrideAndEnablePackOptions", overrideAndEnablePackOptions),
      );
    }, [
      openRightPanel,
      panelData,
      readOnly,
      overrideAndEnablePackOptions,
      isDemo,
      showDemoHint,
    ]);

    const openInventoryPackOptions = useCallback(() => {
      if (isDemo) {
        showDemoHint();
        return;
      }

      openRightPanel(
        "PACK_OPTIONS",
        panelData
          .set("preventReLoading", true)
          .set("returnScreen", "STORAGE_ITEM")
          .set("readOnly", readOnly)
          .set("useInventoryAddons", true)
          .set("overrideAndEnablePackOptions", overrideAndEnablePackOptions),
      );
    }, [
      openRightPanel,
      panelData,
      readOnly,
      overrideAndEnablePackOptions,
      isDemo,
      showDemoHint,
    ]);

    const openInventory = useCallback(
      () => {
        const branchId =
          routeBranchId ||
          shipment.get("branch_id") ||
          panelData.get("branch_id") ||
          originalItem.get("branch_id");
        const returnToPath = branchId ? `/parcels/${branchId}` : "/parcels";
        const restoreStorageParams = (() => {
          if (!storageParams || typeof storageParams.toJS !== "function") {
            return undefined;
          }

          if (typeof storageParams.remove !== "function") {
            return storageParams.toJS();
          }

          return storageParams.remove("page").toJS();
        })();

        openRightPanel(
          "STORAGE_ITEM",
          panelData.set("preventAutoCloseOnNavigationChange", true),
        );

        navigate(`/parcels/inventory/${originalItem.get("id")}`, {
          state: {
            returnToPath,
            restoreStorageParams,
            autoSelectFirstItem: true,
          },
        });
      },
      [
        originalItem,
        navigate,
        openRightPanel,
        panelData,
        shipment,
        storageParams,
        routeBranchId,
      ],
    );

    const handleBack = useCallback(() => {
      openRightPanel(
        panelData.get("returnPreviousPanel"),
        panelData
          .set("id", panelData.get("consolidationId"))
          .set("returnPreviousPanel", panelData.get("rootReturnPanel"))
          .set("sku", panelData.get("consolidationSku")),
      );
    }, [openRightPanel, panelData]);

    const mainTitle = isInventoryItem
      ? itemDetails.get("description")
      : panelData.get("description");
    const pictures = itemDetails.get("pictures", List());
    const weight = itemDetails.get("weight") || "";

    return (
      <ContentWrapper key={id}>
        <Heading>
          {readOnly && (
            <BackButton color={ButtonColor.Black50} onClick={handleBack}>
              <LeftArrowIcon type={IconType.Arrow} />
              {panelData.get("consolidationSku")}:
            </BackButton>
          )}
          <ShipmentLogo
            $readOnly={readOnly}
            image={panelData.get("emblem_thumb_url") || ParcelIcon}
          />
          <Title $readOnly={readOnly}>{mainTitle}</Title>
        </Heading>
        <TabsView>
          <Tab title={t("parcels.details")}>
            <ContentContainer>
              <Container>
                {isLoading && (
                  <SpinnerSection>
                    <Spinner isActive={isLoading} />
                  </SpinnerSection>
                )}
                {!isLoading && (
                  <ImageSection>
                    <ImageViewer2
                      images={pictures.size ? pictures.toJS() : []}
                      preventImageZoom={preventImageZoom}
                    />
                  </ImageSection>
                )}
                <ParcelInfoContainer>
                  <div>
                    <ParcelInfoKey>{t("parcels.receivedDate")}: </ParcelInfoKey>
                    <ParcelInfoValue>{received}</ParcelInfoValue>
                  </div>
                  <div>
                    <ParcelInfoKey>{`${t("parcels.weight")}: `}</ParcelInfoKey>
                    <ParcelInfoValue>{`${weight} ${t(
                      `units.${units}.primaryShort`,
                    )}`}</ParcelInfoValue>
                  </div>
                </ParcelInfoContainer>
                {isInventoryItem && (
                  <>
                    <ParcelInfoContainer>
                      <div>
                        <ParcelInfoKey>{t("parcels.package")}: </ParcelInfoKey>
                        <ParcelInfoValue>
                          {shipment.get("description")}
                        </ParcelInfoValue>
                      </div>
                    </ParcelInfoContainer>
                    {!originalItemLatest && <InventoryItemCommentSkeleton />}
                    {originalItemLatest && (
                      <InventoryItemComment
                        shipment={originalItemLatest}
                        updateComment={updateItemComment}
                      />
                    )}
                    <ServicesButtons
                      shipment={shipment}
                      openPackOptions={openInventoryPackOptions}
                      openServicePanel={openServicePanel}
                      isCartVariant={isCartVariant}
                      isInventoryItemVariant
                      isItemInCart={panelData.get("isItemInCart")}
                      originalItem={originalItem}
                    />
                  </>
                )}
                {!isInventoryItem && (
                  <>
                    {shipment.size <= 0 && <InventoryItemCommentSkeleton />}
                    {shipment.size > 0 && (
                      <ShipmentComment
                        shipment={shipment}
                        updateComment={updateComment}
                      />
                    )}
                    <ServicesButtons
                      shipment={shipment}
                      openPackOptions={openPackOptions}
                      openServicePanel={openServicePanel}
                      openInventory={openInventory}
                      isCartVariant={isCartVariant}
                      hasInventoryItems={hasInventoryItems}
                      originalItem={originalItem}
                    />
                  </>
                )}
              </Container>
            </ContentContainer>
          </Tab>
          <Tab title={t("parcels.customsData")}>
            <Content>
              {isOpen && showShipment && (
                <CustomsDeclarationV2
                  itemId={customsItemId}
                  shipmentId={id}
                  readOnly={readOnly || isDemo}
                  declarations={declarations}
                  create={wrappedCreateCustomsDeclaration}
                  noPadding
                  isShipmentImportCustomsDeclaration
                  isShowHSCodeColumn
                />
              )}
            </Content>
          </Tab>
        </TabsView>
      </ContentWrapper>
    );
  },
);

const withConnect = connect(
  createStructuredSelector({
    isLoading: storageSelectDetailedLoading,
    isLoadingOutgoingShipment: outgoingSelectDetailedShipmentLoading,
    detailedShipment: storageSelectDetailedShipment,
    outgoingDetailedShipment: outgoingSelectDetailedShipment,
    serviceRequestsTypes: requestsSelectServiceTypes,
    addons: servicesSelectAddons,
    historyDetailedShipment: historySelectDetailedShipment,
    isLoadingHistoryShipment: historySelectDetailedShipmentLoading,
    units: authSelectUnits,
    storageParams: storageSelectParams,
  }),
  {
    getRequestTypes,
    getAddonsMethods,
    getConsolidationAddonsMethods,
    createCustomsDeclaration,
    openRightPanel,
  },
);

export default withConnect(StorageItemPanel);
